import DataReferenceCollection from './DataReferenceCollection.js';
import NormalizedDataReference from './NormalizedDataReference.js';
import DataImplementation from './DataImplementation.js';
import DataReferenceSourceType from './DataReferenceSourceType.js';
import DataImplementationOverride from './DataImplementationOverride.js';
import SystemColumn from '../system-metadata/Column.js';
import CustomerColumn from '../customer-metadata/Column.js';
const propertyDef = (propertyName) => {
    return {
        get() {
            const source = this.hasOverrideFor(propertyName) ? this.override : this.defaults;
            return source[propertyName];
        },
        set(value) {
            const override = value === this.defaults[propertyName] ? null : value;
            this.override[propertyName] = override;
        },
    };
};
const propertyNames = ['label', 'unit', 'numericFormat', 'isDisabled'];
/**
 * @memberof data-references
 * @classdesc
 * Overrides a single variable's display properties; from inclusion, to format and labeling.
 *
 * @property {DataReference} ref
 * @property {DataImplementationOverride} override
 * @property {object} defaults
 * @property {(CustomerColumn[]|SystemColumn[])} columns
 */
class DataRecord {
    constructor(props) {
        this.ref = props.ref;
        this.override = props.override || new DataImplementationOverride();
        this.defaults = {};
        const defs = propertyNames.reduce((memo, propertyName) => {
            memo[propertyName] = propertyDef(propertyName);
            return memo;
        }, {});
        Object.defineProperties(this, defs);
        this.unique = DataRecord.count++;
        this.load();
    }
    /**
     * @type {string}
     */
    get key() {
        return this.ref.key;
    }
    /**
     * @type {string}
     */
    get timeLabel() {
        let label = '';
        const hasColumns = this.columns.length > 0;
        if (hasColumns) {
            const [numerator] = this.columns;
            const hasTimeElement = !!numerator.timeElement;
            label = hasTimeElement ? numerator.timeElement.friendlyShortName : label;
        }
        return label;
    }
    /**
     * @type {boolean}
     */
    get isEnabled() {
        return !this.isDisabled;
    }
    /**
     * @type {boolean}
     */
    get isNormalized() {
        return this.ref instanceof NormalizedDataReference;
    }
    /**
     * @type {boolean}
     */
    get canBeNormalized() {
        return !this.isNormalized && this.statisticalType === 'count';
    }
    /**
     * @type {string}
     */
    get statisticalType() {
        return this.columns[0] && this.columns[0].characteristicType.statisticalType;
    }
    get attribution() {
        if (this.columns[0] instanceof SystemColumn) {
            const column = this.columns[0];
            return column.attribution;
        }
        return null;
    }
    get attributions() {
        return this.columns.map(col => (col instanceof SystemColumn ? col.attribution : null)).filter(v => v);
    }
    /**
     * @type {DataReference[]}
     */
    get references() {
        let references = [];
        if (this.isNormalized) {
            const ref = this.ref;
            references = [ref.numerator, ref.denominator];
        }
        else {
            references = [this.ref];
        }
        return references;
    }
    /**
     * @return {Promise}
     */
    ready() {
        return this._ready.promise;
    }
    /**
     * @param {string} field
     * @return {boolean}
     */
    hasOverrideFor(field) {
        const isDefined = this.override.hasOverrideFor(field);
        const isNotDefault = isDefined && this.override[field] !== this.defaults[field];
        return isNotDefault;
    }
    /**
     * @return {boolean}
     */
    hasAnyOverride() {
        return propertyNames.filter(p => p !== 'isDisabled').some(this.hasOverrideFor.bind(this));
    }
    /**
     * @param {CustomerColumn[]|SystemColumn[]} columns
     */
    updateDefaults(columns) {
        this.columns = columns;
        const [column] = this.columns;
        this.defaults = {
            label: column.characteristicType.friendlyLongName,
            unit: column.characteristicType.unit,
            numericFormat: column.characteristicType.numericFormat,
            isDisabled: false,
        };
        if (this.isNormalized) {
            this.defaults.label = makeDefaultNormalizedLabel(this.columns, this.ref);
            this.defaults.unit = '';
        }
        this._ready.resolve(this);
    }
    load() {
        this._ready = {};
        // Configure a promise to use for "ready" indication
        const promise = new Promise((resolve, reject) => {
            this._ready.resolve = resolve;
            this._ready.reject = reject;
        });
        this._ready.promise = promise;
        this.columns = [];
        const load = (this.references.map((ref) => {
            const Column = COLUMN_RECORD_MAP[ref.type];
            return Column.forReference(ref);
        }));
        return Promise.all(load)
            .then(this.updateDefaults.bind(this));
    }
    normalizers() {
        return this.ready().then(() => {
            if (this.columns[0] instanceof SystemColumn) {
                return this.columns[0].getDefaultNormalizers();
            }
            return Promise.resolve([]);
        });
    }
    /**
     * @return {DataImplementationOverride}
     */
    toOverride() {
        return new DataImplementationOverride({ ...this.override });
    }
    /**
     * @return {DataImplementation}
     */
    toImplementation() {
        let numerator = null;
        let denominator = null;
        if (this.isNormalized) {
            const ref = this.ref;
            numerator = ref.numerator;
            denominator = ref.denominator;
        }
        else {
            numerator = this.ref;
        }
        const normalization = this.isNormalized && new DataImplementation.Normalization({
            ref: new DataReferenceCollection({ variables: [denominator] }),
        });
        return new DataImplementation({
            ref: new DataReferenceCollection({ variables: [numerator] }),
            overrides: [this.toOverride()],
            normalization: this.isNormalized ? normalization : null,
        });
    }
    /**
     * @param {DataReference|NormalizedDataReference} ref
     * @param {DataImplementationOverride} override
     * @return {DataRecord}
     */
    static fromReference(ref, override) {
        return new DataRecord({ ref, override });
    }
}
DataRecord.count = 0;
const COLUMN_RECORD_MAP = {
    [DataReferenceSourceType.CUSTOMER]: CustomerColumn,
    [DataReferenceSourceType.SYSTEM]: SystemColumn,
};
function makeDefaultNormalizedLabel([numerator, denominator], ref) {
    const primaryLabel = numerator.characteristicType.friendlyLongName;
    const normalizeByLabel = denominator.characteristicType.friendlyLongName;
    const hasLabels = primaryLabel && normalizeByLabel;
    // Handle initial loading of names by not populating until both are retrieved
    let label = hasLabels ? `${primaryLabel} normalized by ${normalizeByLabel}` : '';
    // Special case where there is a "normalization name" available
    const hasSystemDenominator = ref.denominator.type === DataReferenceSourceType.SYSTEM;
    if (hasLabels && hasSystemDenominator && denominator instanceof SystemColumn) {
        const normalizationName = denominator.characteristicType.normalizationName;
        const hasNormalizationName = !!normalizationName;
        label = hasNormalizationName ? `${primaryLabel} ${normalizationName}` : label;
    }
    return label;
}
export default DataRecord;
